import { useCallback, useEffect, useState } from "react";
import type React from "react";

export const OTP_LENGTH = 6;

/** Keeps ASCII digits only — pasted "123 456", "123-456" or "Code: 123456" all work. */
export function digitsOnly(value: string): string {
  return (value ?? "").replace(/[^0-9]/g, "");
}

/**
 * Six-box one-time-code state for the sender-address dialog.
 *
 * Deliberately NOT `useVerificationForm`: that hook is wired to the auth
 * handlers (resend, change-email, delete-company) and keeps whatever
 * character is typed or pasted. This one accepts digits only, spreads a paste
 * (or an OS one-time-code autofill, which lands as one change event with the
 * whole code) across the boxes, and moves focus by element id.
 */
export function useOtpDigits(idPrefix: string) {
  const [digits, setDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(""));
  const [activeIndex, setActiveIndex] = useState(0);
  // Bumped on every focus request so re-requesting the SAME index still moves
  // focus (e.g. after `clear()` while box 0 was already active).
  const [focusTick, setFocusTick] = useState(0);

  useEffect(() => {
    const el = document.getElementById(`${idPrefix}-${activeIndex}`);
    if (el instanceof HTMLInputElement) el.focus();
  }, [activeIndex, focusTick, idPrefix]);

  const focusIndex = useCallback((index: number) => {
    setActiveIndex(Math.max(0, Math.min(OTP_LENGTH - 1, index)));
    setFocusTick((n) => n + 1);
  }, []);

  /** Writes `incoming` digits starting at `start`; returns the next index. */
  const fillFrom = useCallback(
    (start: number, incoming: string) => {
      const clean = digitsOnly(incoming).slice(0, OTP_LENGTH - start);
      setDigits((prev) => {
        const next = [...prev];
        clean.split("").forEach((d, i) => {
          next[start + i] = d;
        });
        return next;
      });
      return start + clean.length;
    },
    [],
  );

  const handleChange = useCallback(
    (value: string, index: number) => {
      const clean = digitsOnly(value);
      if (clean.length === 0) {
        // A non-digit was typed (ignored) or the box was cleared.
        if (value === "") {
          setDigits((prev) => {
            const next = [...prev];
            next[index] = "";
            return next;
          });
        }
        return;
      }
      if (clean.length >= OTP_LENGTH) {
        // OS one-time-code autofill delivers the whole code into one box.
        const nextIndex = fillFrom(0, clean);
        focusIndex(nextIndex);
        return;
      }
      // Typing over a filled box yields "old+new": keep the newest digit.
      const digit = clean.slice(-1);
      setDigits((prev) => {
        const next = [...prev];
        next[index] = digit;
        return next;
      });
      if (index < OTP_LENGTH - 1) focusIndex(index + 1);
    },
    [fillFrom, focusIndex],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (event.key === "Backspace" && !digits[index] && index > 0) {
        setDigits((prev) => {
          const next = [...prev];
          next[index - 1] = "";
          return next;
        });
        focusIndex(index - 1);
      } else if (event.key === "ArrowLeft" && index > 0) {
        focusIndex(index - 1);
      } else if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
        focusIndex(index + 1);
      }
    },
    [digits, focusIndex],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const clean = digitsOnly(event.clipboardData.getData("text")).slice(0, OTP_LENGTH);
      if (clean.length === 0) return;
      setDigits(() => {
        const next = Array(OTP_LENGTH).fill("");
        clean.split("").forEach((d, i) => {
          next[i] = d;
        });
        return next;
      });
      focusIndex(clean.length);
    },
    [focusIndex],
  );

  const clear = useCallback(() => {
    setDigits(Array(OTP_LENGTH).fill(""));
    focusIndex(0);
  }, [focusIndex]);

  const code = digits.join("");
  const isComplete = /^[0-9]{6}$/.test(code);

  return {
    digits,
    activeIndex,
    code,
    isComplete,
    handleChange,
    handleKeyDown,
    handlePaste,
    clear,
  };
}
